import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  ShoppingCart as CartIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../services/firebase/config";

function GroceryStore() {
  const user = useSelector((state) => state.auth.user);
  const [groceryItems, setGroceryItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    imageUrl: "",
    quantity: "",
  });

  const categories = [
    "Produce",
    "Dairy",
    "Meat & Seafood",
    "Bakery",
    "Pantry",
    "Frozen",
    "Beverages",
    "Snacks",
    "Other",
  ];

  useEffect(() => {
    // Simplified query while indexes are building - will sort client-side
    const q = query(
      collection(db, "groceryItems"),
      where("available", "==", true)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort client-side until indexes are ready
      const sortedItems = items.sort((a, b) => {
        if (a.category !== b.category) {
          return a.category.localeCompare(b.category);
        }
        return a.name.localeCompare(b.name);
      });

      setGroceryItems(sortedItems);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAddToCart = (item) => {
    const existingItem = cart.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      setCart(
        cart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const handleUpdateCartQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      handleRemoveFromCart(itemId);
      return;
    }
    setCart(
      cart.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const handlePlaceOrder = async () => {
    if (!user) {
      alert("Please sign in to place an order.");
      return;
    }

    setOrderLoading(true);
    try {
      // Get user data
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.data();
      const total = calculateTotal();

      if (!userData || userData.neighborhoodDollars < total) {
        alert("Insufficient Neighborhood Dollars for this order.");
        setOrderLoading(false);
        return;
      }

      // Create order
      await addDoc(collection(db, "groceryOrders"), {
        userId: user.uid,
        userEmail: user.email,
        userName: userData.name || "Unknown",
        items: cart,
        total,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      // Deduct neighborhood dollars
      await updateDoc(doc(db, "users", user.uid), {
        neighborhoodDollars: userData.neighborhoodDollars - total,
      });

      // Clear cart and show success
      setCart([]);
      setCartOpen(false);
      setOrderSuccess(true);
    } catch (error) {
      console.error("Error placing order:", error);
      alert("Failed to place order. Please try again.");
    } finally {
      setOrderLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      alert("Please sign in to add items.");
      return;
    }

    try {
      await addDoc(collection(db, "groceryItems"), {
        ...newItem,
        price: Number(newItem.price),
        quantity: Number(newItem.quantity),
        sellerId: user.uid,
        sellerName: user.displayName || user.email,
        available: true,
        createdAt: serverTimestamp(),
      });

      setNewItem({
        name: "",
        description: "",
        price: "",
        category: "",
        imageUrl: "",
        quantity: "",
      });
      setDialogOpen(false);
    } catch (error) {
      console.error("Error adding grocery item:", error);
      alert("Failed to add item. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem({ ...newItem, [name]: value });
  };

  if (loading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          🛒 Traddoo Joe's Grocery
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<CartIcon />}
            onClick={() => setCartOpen(true)}
            sx={{ mr: 2 }}
          >
            Cart ({cart.length})
          </Button>
        </Box>
      </Box>

      {groceryItems.length === 0 ? (
        <Typography variant="h6" textAlign="center" color="text.secondary">
          No grocery items available yet. Be the first to add some!
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {groceryItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ height: "100%" }}>
                {item.imageUrl && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={item.imageUrl}
                    alt={item.name}
                    sx={{ objectFit: "cover" }}
                  />
                )}
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {item.name}
                  </Typography>
                  <Chip
                    label={item.category}
                    color="primary"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {item.description}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {item.price.toFixed(2)} ND
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Available: {item.quantity}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleAddToCart(item)}
                    disabled={item.quantity === 0}
                    fullWidth
                  >
                    Add to Cart
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Item FAB */}
      {user && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: "fixed", bottom: 16, right: 16 }}
          onClick={() => setDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Add Item Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>Add New Grocery Item</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label="Item Name"
                  value={newItem.name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={newItem.category}
                    onChange={handleInputChange}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  value={newItem.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="price"
                  label="Price ($)"
                  type="number"
                  value={newItem.price}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="quantity"
                  label="Quantity Available"
                  type="number"
                  value={newItem.quantity}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  inputProps={{ min: "0" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="imageUrl"
                  label="Image URL (optional)"
                  value={newItem.imageUrl}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Add Item
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Shopping Cart Dialog */}
      <Dialog
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Shopping Cart</DialogTitle>
        <DialogContent>
          {cart.length === 0 ? (
            <Typography>Your cart is empty.</Typography>
          ) : (
            <>
              {cart.map((item) => (
                <Box
                  key={item.id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  p={2}
                  border={1}
                  borderColor="divider"
                  borderRadius={1}
                >
                  <Box>
                    <Typography variant="h6">{item.name}</Typography>
                    <Typography color="text.secondary">
                      {item.price.toFixed(2)} ND each
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleUpdateCartQuantity(item.id, item.quantity - 1)
                      }
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography sx={{ minWidth: "20px", textAlign: "center" }}>
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleUpdateCartQuantity(item.id, item.quantity + 1)
                      }
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveFromCart(item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="h6">
                    {(item.price * item.quantity).toFixed(2)} ND
                  </Typography>
                </Box>
              ))}
              <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                <Typography variant="h5">
                  Total: {calculateTotal().toFixed(2)} ND
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCartOpen(false)}>Close</Button>
          {cart.length > 0 && (
            <Button
              variant="contained"
              onClick={handlePlaceOrder}
              disabled={orderLoading}
            >
              {orderLoading ? <CircularProgress size={24} /> : "Place Order"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={orderSuccess}
        autoHideDuration={6000}
        onClose={() => setOrderSuccess(false)}
      >
        <Alert onClose={() => setOrderSuccess(false)} severity="success">
          Order placed successfully! Check your profile to view order details.
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default GroceryStore;
